import type {
  BulkApproveResponse,
  BulkPreviewResponse,
  BulkSelector,
  CoursesResponse,
  ExamsResponse,
  GradesResponse,
  DecideResponse,
  IgnoreListResponse,
  ItemResponse,
  ListItemsResponse,
  ListProposalsResponse,
  MonthResponse,
  ProposalDecision,
  RunSyncResponse,
  RemindersResponse,
  ScoreResponse,
  SessionResponse,
  SetScoreRequest,
  SyncStatusResponse,
  UpdateItemRequest,
  WeekResponse,
} from '@fall26/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiMutate } from './client.js'
import { newClientId } from './queue.js'
import { applySignOut } from './sign-out-cache.js'

/** Every server call the app makes, in one place. */

export const useSession = () =>
  useQuery({ queryKey: ['session'], queryFn: () => apiGet<SessionResponse>('/auth/session'), retry: false })

export const useCourses = () =>
  useQuery({ queryKey: ['courses'], queryFn: () => apiGet<CoursesResponse>('/courses') })

export const useWeek = (date: string) =>
  useQuery({ queryKey: ['week', date], queryFn: () => apiGet<WeekResponse>(`/weeks/${date}`) })

export const useItems = (query = '') =>
  useQuery({ queryKey: ['items', query], queryFn: () => apiGet<ListItemsResponse>(`/items${query}`) })

export const useMonth = (month: string) =>
  useQuery({ queryKey: ['month', month], queryFn: () => apiGet<MonthResponse>(`/months/${month}`) })

export const useExams = () => useQuery({ queryKey: ['exams'], queryFn: () => apiGet<ExamsResponse>('/exams') })

export const useGrades = () => useQuery({ queryKey: ['grades'], queryFn: () => apiGet<GradesResponse>('/grades') })

export const useSyncStatus = () =>
  useQuery({ queryKey: ['sync-status'], queryFn: () => apiGet<SyncStatusResponse>('/sync/status') })

export const useReminders = () =>
  useQuery({ queryKey: ['reminders'], queryFn: () => apiGet<RemindersResponse>('/reminders') })

/**
 * Ticking something off.
 *
 * Optimistic, and deliberately so: the tap has to feel instant on a phone, and
 * the mutation may well be going into the queue rather than to a server. The
 * cache is patched IN PLACE — the row keeps its position and does not jump out
 * from under the thumb that just hit it.
 */
export function useSetDone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ itemId, done }: { itemId: number; done: boolean }) =>
      apiMutate<unknown>({
        method: 'PUT',
        path: `/items/${itemId}/progress`,
        body: { done },
        description: `${done ? 'Complete' : 'Reopen'} item #${itemId}`,
      }),

    onMutate: async ({ itemId, done }) => {
      await queryClient.cancelQueries({ queryKey: ['items'] })
      const snapshots = queryClient.getQueriesData({ queryKey: ['items'] })

      queryClient.setQueriesData<ListItemsResponse>({ queryKey: ['items'] }, (old) =>
        old ? { ...old, items: old.items.map((i) => (i.id === itemId ? { ...i, done } : i)) } : old,
      )
      queryClient.setQueriesData<WeekResponse>({ queryKey: ['week'] }, (old) =>
        old
          ? {
              ...old,
              days: old.days.map((d) => ({
                ...d,
                due: d.due.map((i) => (i.id === itemId ? { ...i, done } : i)),
              })),
              dueThisWeek: old.dueThisWeek.map((i) => (i.id === itemId ? { ...i, done } : i)),
            }
          : old,
      )
      return { snapshots }
    },

    onError: (_error, _variables, context) => {
      for (const [queryKey, data] of context?.snapshots ?? []) queryClient.setQueryData(queryKey, data)
    },
  })
}

export function useCreateItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { courseId: string; title: string; dueDate?: string | null; detail?: string }) =>
      apiMutate<unknown>({
        method: 'POST',
        path: '/items',
        // The client names the row, so an offline create can be edited before
        // the server has ever heard of it.
        body: { clientId: newClientId(), ...input },
        description: `Add "${input.title}"`,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['items'] }),
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (password: string) => {
      const res = await fetch(`${import.meta.env.DEV ? '/api' : ''}/auth/login`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null
        throw new Error(body?.error?.message ?? 'could not sign in')
      }
      return res.json()
    },
    onSuccess: () => void queryClient.invalidateQueries(),
  })
}

// ---------------------------------------------------------------------------
// the approval queue
// ---------------------------------------------------------------------------

export const useProposals = (includeSkipped: boolean) =>
  useQuery({
    queryKey: ['proposals', includeSkipped],
    queryFn: () =>
      apiGet<ListProposalsResponse>(`/sync/proposals?includeSkipped=${includeSkipped ? 'true' : 'false'}`),
  })

export const useIgnored = () =>
  useQuery({ queryKey: ['ignored'], queryFn: () => apiGet<IgnoreListResponse>('/sync/ignored') })

/**
 * The preview behind a bulk button.
 *
 * Fetched up front rather than on the tap, because the count is the label — a
 * button that says "Add 12 new items" is a different promise from one that says
 * "Add all", and I should be reading the number before I decide, not after.
 */
export const useBulkPreview = (selector: BulkSelector, enabled: boolean) =>
  useQuery({
    queryKey: ['bulk-preview', selector],
    queryFn: () => apiGet<BulkPreviewResponse>(`/sync/bulk/${selector}`),
    enabled,
  })

/**
 * Everything a decision can touch.
 *
 * Approving an add creates an item; applying an update moves a due date; a
 * delete removes a row from the week. Rather than reason about which views each
 * decision kind reaches, every one of them is refetched — this runs at most a
 * few times per sync session, not per keystroke.
 */
function useQueueInvalidation() {
  const queryClient = useQueryClient()
  return () => {
    for (const key of ['proposals', 'ignored', 'sync-status', 'bulk-preview', 'items', 'week', 'month', 'exams', 'grades'])
      void queryClient.invalidateQueries({ queryKey: [key] })
  }
}

export function useDecide() {
  const invalidate = useQueueInvalidation()
  return useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: ProposalDecision; description: string }) =>
      apiMutate<DecideResponse>({
        method: 'POST',
        path: `/sync/proposals/${id}/decide`,
        body: { decision },
        description: `Review #${id}`,
      }),
    onSettled: invalidate,
  })
}

export function useRunSync() {
  const invalidate = useQueueInvalidation()
  return useMutation({
    mutationFn: () =>
      apiMutate<RunSyncResponse>({
        method: 'POST',
        path: '/sync/run',
        body: { dryRun: false },
        description: 'Sync with Canvas',
      }),
    onSettled: invalidate,
  })
}

export function useBulkApprove() {
  const invalidate = useQueueInvalidation()
  return useMutation({
    mutationFn: (selector: BulkSelector) =>
      apiMutate<BulkApproveResponse>({
        method: 'POST',
        path: '/sync/bulk',
        body: { selector },
        description: `Bulk approve (${selector})`,
      }),
    onSettled: invalidate,
  })
}

export function useUnignore() {
  const invalidate = useQueueInvalidation()
  return useMutation({
    mutationFn: (externalId: string) =>
      apiMutate<unknown>({
        method: 'DELETE',
        path: `/sync/ignored/${encodeURIComponent(externalId)}`,
        description: 'Un-ignore',
      }),
    onSettled: invalidate,
  })
}

// ---------------------------------------------------------------------------
// editing an item
// ---------------------------------------------------------------------------

/**
 * Everything an edit can change on screen.
 *
 * A date lands in the week, the month, the exam countdown, the grade standing
 * and the item's own group all at once, so rather than reason about which of
 * those a given field touches, they are all refetched. This runs on a save, not
 * on a keystroke.
 */
function useItemInvalidation() {
  const queryClient = useQueryClient()
  return () => {
    for (const key of ['items', 'week', 'month', 'exams', 'grades']) {
      void queryClient.invalidateQueries({ queryKey: [key] })
    }
  }
}

/**
 * Edit fields on an item.
 *
 * The server records an override row for every field named here, which is what
 * stops a later Canvas sync putting its own value back — the merge sees the
 * field is mine and raises a divergence for the review queue instead of
 * overwriting. So a PATCH is not just a write; it is a claim.
 */
export function useUpdateItem() {
  const invalidate = useItemInvalidation()
  return useMutation({
    mutationFn: ({ id, patch, description }: { id: number; patch: UpdateItemRequest; description: string }) =>
      apiMutate<ItemResponse>({ method: 'PATCH', path: `/items/${id}`, body: patch, description }),
    onSettled: invalidate,
  })
}

/**
 * Enter or clear a score, as points or a percent. The body goes to the server
 * unchanged -- it owns the division. `{pct: null}` deletes the row rather than
 * storing a zero.
 */
export function useSetScore() {
  const invalidate = useItemInvalidation()
  return useMutation({
    mutationFn: ({ id, body, title }: { id: number; body: SetScoreRequest; title: string }) =>
      apiMutate<ScoreResponse>({
        method: 'PUT',
        path: `/items/${id}/score`,
        body,
        description: 'pct' in body && body.pct === null ? `Clear score on "${title}"` : `Score "${title}"`,
      }),
    onSettled: invalidate,
  })
}

/** One number for a category with no items -- ME 334 attendance. */
export function useSetCategoryScore() {
  const invalidate = useItemInvalidation()
  return useMutation({
    mutationFn: ({ courseId, key, body }: { courseId: string; key: string; body: SetScoreRequest }) =>
      apiMutate<unknown>({
        method: 'PUT',
        path: `/courses/${courseId}/categories/${key}/score`,
        body,
        description: `Score ${courseId} ${key}`,
      }),
    onSettled: invalidate,
  })
}

export function useDeleteItem() {
  const invalidate = useItemInvalidation()
  return useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) =>
      apiMutate<unknown>({ method: 'DELETE', path: `/items/${id}`, description: `Delete "${title}"` }),
    onSettled: invalidate,
  })
}

/**
 * Sign out.
 *
 * Deliberately NOT through the mutation queue. Queueing a sign-out would mean
 * the credential outlives the tap that revoked it, and a request that clears a
 * cookie has nothing to replay — if it did not reach the server, I am still
 * signed in and should be told so rather than reassured.
 */
export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${import.meta.env.DEV ? '/api' : ''}/auth/logout`, {
        method: 'POST',
        credentials: 'same-origin',
      })
      if (!res.ok) throw new Error('could not sign out')
      return res.json()
    },
    // The order is load-bearing and lives in its own module with its own test.
    onSuccess: () => applySignOut(queryClient),
  })
}
